/**
 * Companies House lookup (F8): a `company_number` field fills and locks the
 * company name + registered address it is configured for, and only an active
 * company may apply. Shared by the Worker (which enforces it), the public
 * renderer and the admin.
 */

import type { FieldDef } from './fields.js';
import { addressKey, filledAddressKeys } from './fields.js';

/** A registered office address, in the form's address-value shape. */
export interface CompanyAddress {
  line1: string;
  line2: string;
  line3: string;
  city: string;
  county: string;
  postcode: string;
  /** Always GB — a UK company's registered office is in the UK. */
  country: string;
}

/** The parts of a Companies House company profile the forms use. */
export interface CompanyProfile {
  /** Normalised company number, e.g. `01234567`, `SC123456`. */
  number: string;
  name: string;
  /** Companies House `company_status`, e.g. `active`, `dissolved`. */
  status: string;
  /** Companies House `type`, e.g. `ltd`, `plc`, `llp`. */
  type: string;
  /** `YYYY-MM-DD`, when known. */
  incorporatedOn: string | null;
  sicCodes: string[];
  address: CompanyAddress;
}

/** What the Worker recorded for one company-number field at submit. */
export interface CompanyCheck {
  number: string;
  checkedAt: string;
  /** `unavailable` = Companies House could not be reached; flagged to staff. */
  outcome: 'verified' | 'unavailable';
  profile?: CompanyProfile;
}

/**
 * Reserved key in a submission's customer data holding the checks, keyed by
 * company-number field id. Not a field: everything else reads fields by id.
 */
export const COMPANY_CHECKS_KEY = '_companiesHouse';

/** The country code stored on a filled registered address. */
export const COMPANY_ADDRESS_COUNTRY = 'GB';

/**
 * Normalise a typed company number: spaces dropped, upper-cased, digits
 * zero-padded (`1234567` → `01234567`, `SC12345` → `SC012345`). Returns null
 * unless it is 8 digits or 2 letters + 6 digits.
 */
export function normaliseCompanyNumber(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.replace(/\s+/g, '').toUpperCase();
  let m = /^(\d{1,8})$/.exec(s);
  if (m) return m[1]!.padStart(8, '0');
  m = /^([A-Z]{2})(\d{1,6})$/.exec(s);
  if (m) return m[1]! + m[2]!.padStart(6, '0');
  return null;
}

/** Whether Companies House lists the company as active (others get a warning). */
export function isActiveCompany(status: string): boolean {
  return status === 'active';
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  dissolved: 'Dissolved',
  liquidation: 'In liquidation',
  receivership: 'In receivership',
  administration: 'In administration',
  'voluntary-arrangement': 'In a voluntary arrangement',
  'converted-closed': 'Converted or closed',
  'insolvency-proceedings': 'In insolvency proceedings',
  registered: 'Registered',
  removed: 'Removed',
  closed: 'Closed',
  open: 'Open',
};

export function companyStatusLabel(status: string): string {
  return STATUS_LABELS[status] ?? humanise(status);
}

const TYPE_LABELS: Record<string, string> = {
  ltd: 'Private limited company',
  plc: 'Public limited company',
  llp: 'Limited liability partnership',
  'private-unlimited': 'Private unlimited company',
  'private-limited-guarant-nsc': 'Private company limited by guarantee',
  'private-limited-guarant-nsc-limited-exemption': 'Private company limited by guarantee',
  'limited-partnership': 'Limited partnership',
  'charitable-incorporated-organisation': 'Charitable incorporated organisation',
  'registered-society-non-jurisdictional': 'Registered society',
  'uk-establishment': 'UK establishment of an overseas company',
  'oversea-company': 'Overseas company',
};

export function companyTypeLabel(type: string): string {
  return TYPE_LABELS[type] ?? humanise(type);
}

function humanise(s: string): string {
  const t = s.replace(/-/g, ' ').trim();
  return t ? t[0]!.toUpperCase() + t.slice(1) : '';
}

/**
 * The applicant-facing warning for a company that isn't active (Hayden,
 * 23/10/2026: warn, don't block; JFH checks it before approving).
 */
export function companyNotActiveMessage(p: Pick<CompanyProfile, 'name' | 'status'>): string {
  return `Companies House lists ${companyNameCase(p.name)} as ${companyStatusLabel(p.status).toLowerCase()}. We've filled in its name and registered address. You can still apply, and we'll check it before opening an account.`;
}

/** The staff warning (Teams card, portal) for a company that isn't active, or null. */
export function companyStaffWarning(check: CompanyCheck | undefined): string | null {
  const p = check?.profile;
  if (!p || isActiveCompany(p.status)) return null;
  return `Not active: Companies House lists ${p.name} as ${companyStatusLabel(p.status).toLowerCase()}. Check before approving.`;
}

/** The company's public Companies House page. */
export function companiesHouseUrl(number: string): string {
  return `https://find-and-update.company-information.service.gov.uk/company/${encodeURIComponent(number)}`;
}

/** `YYYY-MM-DD` → `DD/MM/YYYY` (UK display). */
export function ukDate(iso: string | null): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/**
 * Fields locked by a company-number lookup: target field id → the company
 * field that fills it. Only targets that exist with the right type count.
 */
export function companyLockTargets(fields: FieldDef[]): Map<string, string> {
  const byId = new Map(fields.map((f) => [f.id, f] as const));
  const out = new Map<string, string>();
  for (const f of fields) {
    if (f.type !== 'company_number') continue;
    const name = f.companyLookup?.nameFieldId;
    const address = f.companyLookup?.addressFieldId;
    if (name && byId.get(name)?.type === 'short_text') out.set(name, f.id);
    if (address && byId.get(address)?.type === 'address') out.set(address, f.id);
  }
  return out;
}

/**
 * Fields an application's Companies House check filled in (name, registered
 * address). Their address lines are whatever the register holds (often no
 * county), so required lines aren't enforced on them: the form and the
 * Worker's submit check already skip them, and the Teams card must too.
 */
export function companyFilledFields(fields: FieldDef[], values: Record<string, unknown>): Set<string> {
  const checks = companyChecksOf(values);
  const out = new Set<string>();
  for (const [target, source] of companyLockTargets(fields)) {
    if (checks[source]?.outcome === 'verified') out.add(target);
  }
  return withCopiedAddresses(fields, values, out);
}

/**
 * `filled` plus the addresses that are an exact copy of one of its addresses
 * ("Same as registered address"): a copy of the register's address has the
 * same gaps (often no county), so it gets the same leniency. Contact
 * addresses get it through `filledAddressKeys` + `repeaterErrors`.
 */
export function withCopiedAddresses(
  fields: FieldDef[],
  values: Record<string, unknown>,
  filled: ReadonlySet<string>,
): Set<string> {
  const out = new Set(filled);
  const sources = filledAddressKeys(fields, values, filled);
  if (!sources.length) return out;
  for (const f of fields) {
    if (f.type === 'address' && !out.has(f.id) && sources.includes(addressKey(values[f.id]))) out.add(f.id);
  }
  return out;
}

const NAME_SUFFIXES: Record<string, string> = {
  LTD: 'Ltd',
  'LTD.': 'Ltd.',
  LIMITED: 'Limited',
  PLC: 'PLC',
  LLP: 'LLP',
  LP: 'LP',
  CIC: 'CIC',
  CO: 'Co',
  'CO.': 'Co.',
  UK: 'UK',
  GB: 'GB',
};
const NAME_SMALL = new Set(['AND', 'OF', 'THE', 'FOR', 'IN', 'ON', 'AT', 'BY', 'TO']);

/**
 * A Companies House name as people write it (Hayden, 26/10/2026): the
 * register keeps names in capitals ("VITAKO LTD" → "Vitako Ltd"). Company
 * endings keep their usual form (Ltd, PLC, LLP…), words without vowels stay
 * capitals as abbreviations (JFH, "(UK)"), small joining words are lower case
 * except first, and hyphens / apostrophes start a new capital (O'Neill,
 * Smith-Jones). A name that isn't all capitals is left as it is.
 */
export function companyNameCase(name: string): string {
  if (!name || name !== name.toUpperCase() || !/[A-Z]/.test(name)) return name;
  const word = (w: string, first: boolean): string => {
    const core = w.replace(/^[^A-Z0-9]+|[^A-Z0-9.]+$/g, '');
    const lead = w.slice(0, w.indexOf(core.charAt(0)));
    const tail = w.slice(lead.length + core.length);
    if (!core) return w;
    if (NAME_SUFFIXES[core]) return lead + NAME_SUFFIXES[core] + tail;
    if (!first && NAME_SMALL.has(core)) return lead + core.toLowerCase() + tail;
    if (/^[A-Z]+$/.test(core) && !/[AEIOUY]/.test(core)) return w;
    return w.toLowerCase().replace(/(^|[\s\-'’(&/.])([a-z])/g, (_m, sep: string, c: string) => sep + c.toUpperCase());
  };
  return name
    .split(/(\s+)/)
    .map((part, i) => (/^\s+$/.test(part) ? part : word(part, i === 0)))
    .join('');
}

/** The registered address as a value for `target` (country only if it shows one). */
export function companyAddressValue(
  address: CompanyAddress,
  target: Pick<FieldDef, 'address'>,
): Record<string, string> {
  const showsCountry = (target.address?.components ?? []).some(
    (c) => c.key === 'country' && c.enabled,
  );
  return { ...address, country: showsCountry ? address.country : '' };
}

/** The company checks recorded on a submission's customer data. */
export function companyChecksOf(data: Record<string, unknown>): Record<string, CompanyCheck> {
  const raw = data[COMPANY_CHECKS_KEY];
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, CompanyCheck>)
    : {};
}

/** One-line staff summary of a check, e.g. "Active · Private limited company · incorporated 01/02/2010". */
export function companyCheckSummary(check: CompanyCheck | undefined): string {
  if (!check) return 'Not checked with Companies House';
  if (check.outcome === 'unavailable' || !check.profile) {
    return 'NOT CHECKED: Companies House was unavailable when this was submitted, so check the company by hand';
  }
  const p = check.profile;
  const inc = ukDate(p.incorporatedOn);
  return [companyStatusLabel(p.status), companyTypeLabel(p.type), inc && `incorporated ${inc}`]
    .filter(Boolean)
    .join(' · ');
}
